import Point from '../../Utility/Point';
import Renderable from '../Renderable';
import PIXIRenderer from '../../System/Render/PIXIRenderer';
import ObservablePoint from '../../Utility/ObservablePoint';
import Rope from 'Prototype/Rope';
import { VNode, Constraint } from '../../System/Animation/Clip/RopePhysics';
import Sprite from '../../Prototype/Sprite';
import TransformComponent from '../Transform';
import { TilingRope } from 'Prototype/TilingRope';
import ConnectedModifier from '../../Prototype/Level/Card/Modifier/Connected';
import BaseCard from '../../Prototype/Level/Card/BaseCard';

const DEBUG = false;

export default class RopeComponent extends Renderable<TilingRope, Rope> {
    /* ------------------------------- Rope Stuff ------------------------------- */
    private gravity: number = 5;
    private stiffness: number = 2;
    private nodes: VNode[];
    private prevNodePositions: Point[] = [];
    private constraints: Constraint[];
    private iconA: Sprite = undefined;
    private iconB: Sprite = undefined;
    private graphics: PIXI.Graphics | null = null;
    private physicsEnabled: boolean = true;
    private folding: boolean = false;
    private followCards: boolean = false;
    private cardA: BaseCard;
    private cardB: BaseCard;
    private hashCardA: string;
    private hashCardB: string;
    private framesInRest: number = 0;

    public attach(gameObject: Rope, params: { points: Point[], cardA: BaseCard; cardB: BaseCard }): void {
        super.attach(gameObject, params);

        if (DEBUG) {
            this.graphics = new PIXI.Graphics();
            PIXIRenderer.rootContainer.addChild(this.graphics);
        }

        const { points, cardA, cardB } = params;
        this.cardA = cardA;
        this.cardB = cardB;
        this.nodes = [];
        this.constraints = [];

        for (let i = 0, last = points.length - 1; i < points.length; i++) {
            const point = points[i];
            const node = new VNode();
            node.mass = 0.1;
            node.fixed = (i === 0 || i === last);
            node.gravity = this.gravity;
            node.oldX = node.x = point.x;
            node.oldY = node.y = point.y;

            this.nodes.push(node);
        }

        this.makeConstraints();

        this.gameObject.addChild(this.iconA);
        this.gameObject.addChild(this.iconB);
    }

    public resetNodes(): void {
        const pointA = this.cardA.getComponent(TransformComponent).toGlobal(ConnectedModifier.ICON_OFFSET);
        const pointB = this.cardB.getComponent(TransformComponent).toGlobal(ConnectedModifier.ICON_OFFSET);

        this.nodes.forEach((node, i) => {
            node.x = pointA.x + (pointB.x - pointA.x) / (this.nodes.length - 1) * i;
            node.y = pointA.y + (pointB.y - pointA.y) / (this.nodes.length - 1) * i;
        });
    }

    public setEdgePoints(pointA: Point, pointB: Point): void {
        this.startNode.x = pointA.x;
        this.startNode.y = pointA.y;
        this.endNode.x = pointB.x;
        this.endNode.y = pointB.y;

        const goTransform = this.gameObject.getComponent(TransformComponent);
        goTransform.toLocal(pointA, void 0, this.iconA.getComponent(TransformComponent).position);
        goTransform.toLocal(pointB, void 0, this.iconB.getComponent(TransformComponent).position);
    }

    public getNodes(): VNode[] {
        return this.nodes;
    }

    public startFolding(): void {
        this.physicsEnabled = false;
        this.folding = true;
    }

    public startFollowCards(): void {
        this.followCards = true;
    }

    public updatePhysics(): void {
        const hashA = this.getCardHash(this.cardA);
        const hashB = this.getCardHash(this.cardB);
        const cardMoved = hashA !== this.hashCardA || hashB !== this.hashCardB;

        if (cardMoved) {
            this.hashCardA = hashA;
            this.hashCardB = hashB;
        }

        if (this.followCards && cardMoved) {
            this.setEdgePoints(
                this.cardA.getComponent(TransformComponent).toGlobal(ConnectedModifier.ICON_OFFSET),
                this.cardB.getComponent(TransformComponent).toGlobal(ConnectedModifier.ICON_OFFSET),
            );
        }

        if (this.folding && cardMoved) {
            this.nodes.forEach((node, i) => {
                const x = this.startNode.x + (this.endNode.x - this.startNode.x) / (this.nodes.length - 1) * i;
                const y = this.startNode.y + (this.endNode.y - this.startNode.y) / (this.nodes.length - 1) * i;
                node.x += (x - node.x) * 0.6;
                node.y += (y - node.y) * 0.6;
            });
        }

        if (this.physicsEnabled && this.framesInRest < 3) {
            for (let n of this.nodes) {
                n.integrate();
            }

            for (let i = 0; i < 5; i++) {
                for (let n of this.constraints) {
                    n.solve();
                }
            }
        }

        const nodeMoved = this.nodes.some((node, i) => {
            const prev = this.prevNodePositions[i];
            return !prev || Math.abs(node.x - prev.x) > 1 || Math.abs(node.y - prev.y) > 1;
        });

        this.framesInRest = nodeMoved ? 0 : this.framesInRest + 1;

        if (nodeMoved) {
            const points = this.nodes.map((node) => {
                const point = new Point(node.x, node.y);
                this.container.toLocal(point, void 0, point);

                return point;
            });

            this.prevNodePositions = this.nodes.map((n) => new Point(n.x, n.y));
            this.container.points = points;
            this.container.update();
        }

        this.container.cacheAsBitmap = !nodeMoved && !cardMoved && !this.container.displayParent;

        if (this.graphics) {
            this.graphics.clear();
            this.graphics.beginFill(0xffffff, 1);

            this.nodes.forEach((node) => {
                this.graphics.drawCircle(node.x, node.y, 4);
            });
        }
    }

    private getCardHash(card: BaseCard): string {
        return `${card.container.x}|${card.container.y}|${card.container.rotation}|${card.container.scale.x}|${card.container.alpha}`;
    }

    public setGravity(value: number): void {
        this.gravity = value;

        this.nodes.forEach((node) => {
            node.gravity = value;
        });
    }

    public setMass(value: number): void {
        this.nodes.forEach((node) => {
            node.mass = value;
        });
    }

    public setStiffness(value: number): void {
        this.stiffness = value;

        this.constraints.forEach((constraint) => {
            constraint.stiffness = value;
        });
    }

    private makeConstraints(): void {
        for (let i = 1; i < this.nodes.length; i++) {
            this.constraints.push(
                new Constraint(this.nodes[i - 1], this.nodes[i], this.stiffness),
            );
        }
    }

    public get startNode(): VNode {
        return this.nodes[0];
    }

    public get endNode(): VNode {
        return this.nodes[this.nodes.length - 1];
    }

    /* ------------------------------ Texture Stuff ----------------------------- */
    private _texture: string;

    get texture(): string {
        return this._texture;
    }

    set texture(value: string) {
        this._texture = value;

        this.container.texture = PIXIRenderer.getTexture(value);
    }

    get pivot(): Point | ObservablePoint {
        return this.container.pivot as Point | ObservablePoint;
    }

    set pivot(value: Point | ObservablePoint) {
        this.container.pivot.set(value.x, value.y);
    }

    get width(): number {
        return this.container.width;
    }

    set width(value: number) {
        this.container.width = value;
    }

    get height(): number {
        return this.container.height;
    }

    set height(value: number) {
        this.container.height = value;
    }

    get tint(): number {
        return this.container.tint;
    }

    set tint(value: number) {
        this.container.tint = value;
    }
}
